// src/pages/VehicleV2/ServiceExpiryAlert.jsx

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaSave, FaCheckSquare, FaSquare, FaTimes } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/serviceExpiryAlert";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const defaultAlerts = {
  insurance: { enabled: false, days: 0, software: false, sms: false, email: false },
  fc: { enabled: false, days: 0, software: false, sms: false, email: false },
  roadTax: { enabled: false, days: 0, software: false, sms: false, email: false },
  permit: { enabled: false, days: 0, software: false, sms: false, email: false },
  periodicHr: { enabled: false, value: 0, software: false, sms: false, email: false },
  periodicKm: { enabled: false, value: 0, software: false, sms: false, email: false },
};

const ownVehicleRows = [
  { key: "insurance", label: "Insurance" },
  { key: "fc", label: "FC" },
  { key: "roadTax", label: "Road Tax" },
  { key: "permit", label: "Permit" },
];

const periodicRows = [
  { key: "periodicHr", label: "Periodic-HR" },
  { key: "periodicKm", label: "Periodic-KM" },
];

const ServiceExpiryAlert = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const branchActive = useMemo(() => {
    const v = sessionStorage.getItem("BranchActive") || localStorage.getItem("BranchActive");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const [allowed, setAllowed] = useState(true);
  const [canSaveRight, setCanSaveRight] = useState(true);
  const [alerts, setAlerts] = useState(defaultAlerts);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const canSave = !!branchActive && !!canSaveRight;
  const selectAllUsers = users.length > 0 && users.every((u) => !!u.selected);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const Checkbox = ({ checked, onChange, disabled = false }) => (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`flex items-center justify-center w-5 h-5 ${
        disabled ? "opacity-60 cursor-not-allowed" : ""
      }`}
    >
      {checked ? (
        <FaCheckSquare className="w-5 h-5 text-blue-600" />
      ) : (
        <FaSquare className="w-5 h-5 text-gray-400" />
      )}
    </button>
  );

  const ToggleButton = ({ enabled, onChange, disabled = false }) => (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      disabled={disabled}
      className={`w-12 h-6 rounded-full transition-colors duration-200 flex items-center ${
        enabled ? `${buttonClass} justify-end` : "bg-gray-300 dark:bg-gray-600 justify-start"
      } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      <div className="w-4 h-4 rounded-full bg-white mx-1" />
    </button>
  );

  const handleAlertChange = useCallback((alertKey, field, value) => {
    setAlerts((prev) => ({
      ...prev,
      [alertKey]: { ...prev[alertKey], [field]: value },
    }));
  }, []);

  const handleUserSelect = useCallback((id) => {
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, selected: !u.selected } : u)));
  }, []);

  const handleSelectAllUsers = useCallback(() => {
    const next = !selectAllUsers;
    setUsers((prev) => prev.map((u) => ({ ...u, selected: next })));
  }, [selectAllUsers]);

  const loadConfig = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/config`, {
      params: { loginUserId },
      headers: commonHeaders,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to load config");
    setAllowed(data?.allowed !== false);
    setCanSaveRight(!!data?.canSave);
    return data;
  }, [loginUserId, commonHeaders]);

  const loadPageData = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/data`, {
      params: { loginUserId },
      headers: commonHeaders,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to load service expiry data");
    setAlerts(data?.alerts ? { ...defaultAlerts, ...data.alerts } : defaultAlerts);
    setUsers(Array.isArray(data?.users) ? data.users : []);
    if (data?.canSave !== undefined) setCanSaveRight(!!data.canSave);
  }, [loginUserId, commonHeaders]);

  const loadAll = useCallback(async () => {
    try {
      setPageLoading(true);
      const cfg = await loadConfig();
      if (cfg?.allowed === false) return;
      await loadPageData();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load");
    } finally {
      setPageLoading(false);
    }
  }, [loadConfig, loadPageData]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleClear = () => {
    setAlerts(defaultAlerts);
    setUsers((prev) => prev.map((u) => ({ ...u, selected: false })));
  };

  const handleSave = async () => {
    if (!branchActive) return toast.error("Branch is not active. Save not allowed.");
    if (!canSaveRight) return toast.error("You do not have rights to save.");

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API_BASE}/save`,
        {
          loginUserId: toInt(loginUserId, 0),
          alerts,
          selectedUserIds: users.filter((u) => !!u.selected).map((u) => u.id),
        },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Failed to save");
      toast.success(data?.message || "Saved Successfully");
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const renderAlertRows = (list, valueKey) =>
    list.map((service, idx) => (
      <tr
        key={service.key}
        className={`${
          idx % 2 === 0
            ? isDark
              ? "bg-gray-800"
              : "bg-white"
            : isDark
            ? "bg-gray-750"
            : "bg-gray-50"
        } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
      >
        <td className="px-6 py-3 text-sm font-medium">{service.label}</td>
        <td className="px-6 py-3">
          <div className="flex justify-center">
            <ToggleButton
              enabled={!!alerts[service.key]?.enabled}
              onChange={(value) => handleAlertChange(service.key, "enabled", value)}
              disabled={pageLoading || loading || !canSave}
            />
          </div>
        </td>
        <td className="px-6 py-3">
          <input
            type="number"
            value={alerts[service.key]?.[valueKey] ?? 0}
            onChange={(e) => handleAlertChange(service.key, valueKey, toInt(e.target.value, 0))}
            className={`w-24 text-center mx-auto ${inputClass}`}
            disabled={!alerts[service.key]?.enabled || pageLoading || loading || !canSave}
          />
        </td>
        {["software", "sms", "email"].map((field) => (
          <td key={field} className="px-6 py-3">
            <div className="flex justify-center">
              <Checkbox
                checked={!!alerts[service.key]?.[field]}
                onChange={() => handleAlertChange(service.key, field, !alerts[service.key]?.[field])}
                disabled={pageLoading || loading || !canSave}
              />
            </div>
          </td>
        ))}
      </tr>
    ));

  if (!allowed) {
    return (
      <VehicleLayoutWrapper>
        <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
          <HeaderBar
            title={<span className="font-dmserif text-3xl">SERVICE EXPIRY ALERT</span>}
            setSkin={setSkin}
            currentSkin={currentSkin}
          />
          <div
            className={`p-6 mt-6 rounded-2xl shadow-xl border text-center ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
                : "bg-white text-black"
            }`}
          >
            <p className="text-lg font-semibold mb-6">You dont have rights to access this page</p>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold ${
                isDark
                  ? "bg-gray-700 text-white border border-gray-600"
                  : "bg-gray-100 text-gray-700 border border-gray-300"
              }`}
            >
              <FaArrowLeft /> Back
            </button>
          </div>
          <ToastContainer />
        </div>
      </VehicleLayoutWrapper>
    );
  }

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SERVICE EXPIRY ALERT</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Service Expiry Alert Settings
              </h2>
              {pageLoading && (
                <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</p>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft /> Back
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={loading || pageLoading || !canSave}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${
                  loading || pageLoading || !canSave ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass}`}
              >
                <FaSave />
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>

          <Section title="Own Vehicle Expiry Alert" isDark={isDark}>
            <div className="overflow-x-auto rounded-lg border">
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    {["Service", "Enable", "Days", "Software", "SMS", "Email"].map((h) => (
                      <th key={h} className="px-6 py-3 text-sm font-semibold text-center first:text-left">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {renderAlertRows(ownVehicleRows, "days")}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Periodic Expiry Alert" isDark={isDark}>
            <div className="overflow-x-auto rounded-lg border">
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    {["Service", "Enable", "Value", "Software", "SMS", "Email"].map((h) => (
                      <th key={h} className="px-6 py-3 text-sm font-semibold text-center first:text-left">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {renderAlertRows(periodicRows, "value")}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Alert Recipients" isDark={isDark}>
            <div className="flex items-center justify-end gap-3 mb-4">
              <span className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Select All
              </span>
              <Checkbox
                checked={selectAllUsers}
                onChange={handleSelectAllUsers}
                disabled={pageLoading || loading || !canSave}
              />
            </div>
            <div className="overflow-x-auto rounded-lg border max-h-96 overflow-y-auto">
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white sticky top-0`}>
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold">User Name</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Mobile No</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Email</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold w-16">Select</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-sm opacity-70">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((user, idx) => (
                      <tr
                        key={user.id}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className="px-6 py-3 text-sm">{toStr(user.userName)}</td>
                        <td className="px-6 py-3 text-sm">{toStr(user.mobileNo)}</td>
                        <td className="px-6 py-3 text-sm break-all">{toStr(user.email)}</td>
                        <td className="px-6 py-3">
                          <div className="flex justify-center">
                            <Checkbox
                              checked={!!user.selected}
                              onChange={() => handleUserSelect(user.id)}
                              disabled={pageLoading || loading || !canSave}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Section>

          <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-3">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClear}
                disabled={loading || pageLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <FaTimes /> Clear
              </button>
              <button
                type="button"
                onClick={loadAll}
                disabled={loading || pageLoading}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                Reset
              </button>
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || pageLoading || !canSave}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${
                loading || pageLoading || !canSave ? "opacity-60 cursor-not-allowed" : ""
              } ${buttonClass} ${hoverClass}`}
            >
              <FaSave />
              Save Alert Settings
            </button>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

function Section({ title, isDark, children }) {
  return (
    <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
        {title}
      </h3>
      {children}
    </div>
  );
}

export default ServiceExpiryAlert;